import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

/**
 * Full-screen payment verification overlay — shown while we poll Stripe
 * after the checkout redirect (both £1 verdict and £4.99 PDF flows).
 * When `success` is true it flips to a green "Payment confirmed!" state
 * for ~1.8s before the report appears.
 */
export const PaymentVerifyingOverlay = ({ visible, success = false, t }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        key="payment-verifying-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        style={{
          position: "fixed", inset: 0, zIndex: 350,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}
        data-testid="payment-verifying-overlay"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {/* Backdrop — same treatment as AnalyzingOverlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "url('/landing-bg.jpg')",
          backgroundSize: "cover", backgroundPosition: "center",
          filter: "blur(8px)", transform: "scale(1.08)",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.82) 100%)",
        }} />

        <div style={{ position: "relative", zIndex: 2, textAlign: "center", padding: "2rem", maxWidth: 460, width: "100%" }}>
          {success ? (
            /* ── Success state: green check flash ── */
            <motion.div
              key="pay-success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="payment-confirmed-state"
            >
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 16 }}
                className="mx-auto relative flex items-center justify-center"
                style={{ width: 92, height: 92, marginBottom: "1.5rem" }}
              >
                <motion.span
                  animate={{ scale: [1, 1.7], opacity: [0.45, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
                  style={{ position: "absolute", inset: 4, borderRadius: "50%", border: "2px solid #4ade80" }}
                />
                <span
                  className="flex items-center justify-center"
                  style={{
                    width: 76, height: 76, borderRadius: "50%",
                    background: "linear-gradient(135deg,#22C55E 0%,#16A34A 100%)",
                    boxShadow: "0 10px 34px rgba(34,197,94,0.5)",
                  }}
                >
                  <CheckCircle2 className="w-9 h-9" strokeWidth={2.4} style={{ color: "var(--wf-modal-text)" }} />
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                style={{ color: "var(--verdict-color-low)", fontSize: "clamp(1.3rem, 4vw, 1.7rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
                data-testid="payment-confirmed-title"
              >
                {t("paymentConfirmedTitle") || "Payment confirmed!"}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="mt-3"
                style={{ color: "var(--wf-modal-text-muted)", fontSize: "0.92rem", fontWeight: 500 }}
              >
                {t("paymentConfirmedSub") || "Preparing your report…"}
              </motion.p>
            </motion.div>
          ) : (
          <>
          {/* Spinner ring with lock */}
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 18 }}
            className="mx-auto relative flex items-center justify-center"
            style={{ width: 92, height: 92, marginBottom: "1.5rem" }}
          >
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute", inset: 0, borderRadius: "50%",
                border: "3px solid rgba(74,222,128,0.18)",
                borderTopColor: "#4ade80",
              }}
            />
            <motion.span
              animate={{ scale: [1, 1.6], opacity: [0.4, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              style={{ position: "absolute", inset: 6, borderRadius: "50%", border: "2px solid #4ade80" }}
            />
            <span
              className="flex items-center justify-center"
              style={{
                width: 58, height: 58, borderRadius: "50%",
                background: "rgba(34,197,94,0.14)",
                border: "1px solid rgba(74,222,128,0.4)",
              }}
            >
              <Lock className="w-6 h-6" strokeWidth={2.2} style={{ color: "var(--verdict-color-low)" }} />
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            style={{ color: "var(--wf-modal-text)", fontSize: "clamp(1.3rem, 4vw, 1.7rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            data-testid="payment-verifying-title"
          >
            {t("paymentVerifyTitle") || "Checking payment status…"}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mt-3"
            style={{ color: "var(--wf-modal-text-muted)", fontSize: "0.92rem", fontWeight: 500, lineHeight: 1.5 }}
          >
            {t("paymentVerifySub") || "We're confirming your payment with Stripe — this usually takes a few seconds. Please don't close this window."}
          </motion.p>

          {/* Trust footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
            className="mt-6 inline-flex items-center gap-2"
            style={{
              padding: "8px 16px", borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={2.2} style={{ color: "var(--verdict-color-low)" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wf-modal-text-muted)", letterSpacing: "0.03em" }}>
              {t("paymentVerifySecure") || "Secured by Stripe"}
            </span>
          </motion.div>
          </>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
